import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminCsvBulk() {
  const [networks, setNetworks] = useState([]);
  const [scope, setScope] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => { api.get("/networks").then((r) => setNetworks(r.data)); }, []);

  const downloadXlsx = () => {
    const token = localStorage.getItem("tk_token");
    const url = scope === "all" ? `${API}/superadmin/db/export-xlsx` : `${API}/superadmin/db/export-xlsx?network_id=${scope}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(r.statusText); return r.blob(); })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `db_export_${scope}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.click();
        toast.success("Download completato — apri in Excel/Google Sheets");
      })
      .catch((e) => toast.error("Errore download: " + e.message));
  };

  const downloadCsv = () => {
    const token = localStorage.getItem("tk_token");
    fetch(`${API}/csv/template`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `template.csv`;
        link.click();
      });
  };

  const upload = async (e, dry = true) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("dry_run", String(dry));
      const r = await api.post("/csv/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult({ ...r.data, fileName: f.name, dryRun: dry });
      if (dry) toast.success("Preview pronta: verifica errori e clicca 'Applica'");
      else toast.success(`Importato! Prodotti: ${r.data.created_p || 0}/${r.data.updated_p || 0}, Varianti: ${r.data.created_v || 0}/${r.data.updated_v || 0}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8" data-testid="sa-csv-bulk">
        <div className="mb-6">
          <div className="overline text-brand mb-1">SuperAdmin · Bulk Tools</div>
          <h1 className="font-heading font-bold text-3xl">CSV / XLSX — Gestione massiva</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">Esporta lo stato attuale del database in <b>.xlsx</b> con tendine validation (apri in Excel/Sheets), modifica, ricarica.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Export */}
          <div className="bg-white border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
              <Download className="w-4 h-4 text-brand" />
              <h3 className="font-heading font-bold text-base">Scarica database aggiornato</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Genera un file .xlsx in tempo reale con i dati correnti del DB. Le celle con valori controllati avranno menu a tendina (Categoria, Modalità evasione, ecc.).</p>
            <div className="mb-3">
              <label className="text-xs text-muted-foreground mb-1 block">Scope</label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="h-10 rounded-sm" data-testid="export-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le network</SelectItem>
                  {networks.map((n) => <SelectItem key={n.id} value={n.id}>{n.display_name || n.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button onClick={downloadXlsx} data-testid="export-xlsx"
              className="w-full h-11 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand transition-colors inline-flex items-center justify-center gap-2">
              <FileSpreadsheet className="w-4 h-4" /> Scarica .xlsx (con tendine)
            </button>
            <button onClick={downloadCsv} data-testid="export-csv"
              className="mt-2 w-full h-9 rounded-sm border border-border text-xs hover:bg-accent inline-flex items-center justify-center gap-2">
              <Download className="w-3.5 h-3.5" /> Scarica template CSV vuoto
            </button>
          </div>

          {/* Import */}
          <div className="bg-white border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
              <Upload className="w-4 h-4 text-brand" />
              <h3 className="font-heading font-bold text-base">Carica modifiche</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Carica un file .csv o .xlsx modificato. Anteprima dry-run obbligatoria, conferma esplicita per applicare.</p>
            <label className="block border-2 border-dashed border-border rounded-sm p-6 text-center hover:border-brand cursor-pointer transition-colors" data-testid="csv-upload-zone">
              <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-sm font-medium">Trascina o clicca per selezionare</div>
              <div className="text-xs text-muted-foreground mt-1">.csv o .xlsx</div>
              <input type="file" accept=".csv,.xlsx" onChange={(e) => upload(e, true)} className="hidden" disabled={uploading} />
            </label>
            {uploading && <div className="mt-3 text-xs text-center text-muted-foreground">Elaborazione in corso...</div>}
          </div>
        </div>

        {/* Import result */}
        {importResult && (
          <div className="mt-6 bg-white border border-border rounded-sm p-5" data-testid="import-result">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
              <div className="flex items-center gap-2">
                {importResult.summary?.errors?.length > 0 ? <AlertCircle className="w-5 h-5 text-amber-600" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                <div>
                  <div className="font-heading font-bold">Preview: {importResult.fileName}</div>
                  <div className="text-xs text-muted-foreground">{importResult.dryRun ? "DRY-RUN (nessuna modifica scritta)" : "APPLICATO al database"}</div>
                </div>
              </div>
              {importResult.dryRun && importResult.summary?.errors?.length === 0 && (
                <label className="cursor-pointer h-10 px-4 rounded-sm bg-emerald-600 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-emerald-700">
                  <CheckCircle2 className="w-4 h-4" /> Applica modifiche
                  <input type="file" accept=".csv,.xlsx" onChange={(e) => upload(e, false)} className="hidden" data-testid="apply-import" />
                </label>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Stat label="Righe totali" value={importResult.summary?.total_rows} />
              <Stat label="Nuovi prodotti" value={importResult.summary?.to_create_products} highlight />
              <Stat label="Nuove varianti" value={importResult.summary?.to_create_variants} highlight />
              <Stat label="Update prodotti" value={importResult.summary?.to_update_products} />
              <Stat label="Update varianti" value={importResult.summary?.to_update_variants} />
            </div>
            {importResult.summary?.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-3">
                <div className="text-xs font-medium text-red-900 mb-2">Errori ({importResult.summary.errors.length}):</div>
                <div className="space-y-1 max-h-40 overflow-auto">
                  {importResult.summary.errors.slice(0, 20).map((er, i) => (
                    <div key={i} className="text-xs text-red-800 font-mono">Riga {er.row}: {er.error}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className={`p-3 rounded-sm border ${highlight ? "border-foreground bg-foreground text-white" : "border-border bg-white"}`}>
    <div className={`text-[10px] uppercase tracking-wider ${highlight ? "text-white/70" : "text-muted-foreground"}`}>{label}</div>
    <div className="font-heading font-bold text-xl mt-1">{value || 0}</div>
  </div>
);
